import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import type {
  ActorProfile,
  FeedPost,
  RichTextFacet,
  ThreadViewPost,
} from '../../domain/types';
import { commentText } from '../../domain/classic-profile';
import { profileSearch, searchSearch } from '../../lib/route';
import { useAuth } from '../../auth/AuthContext';
import { toggleReaction } from '../../data/authenticated';
import { publicBluesky } from '../../data/public-bluesky';

const InlinePostVideo = lazy(() => import('./InlinePostVideo'));
const PostReplies = lazy(() => import('./PostReplies'));
const QuotedPostCard = lazy(() => import('./QuotedPostCard'));

function stringIndexAtByte(text: string, target: number): number {
  const encoder = new TextEncoder();
  let bytes = 0;
  let index = 0;
  for (const character of text) {
    const size = encoder.encode(character).length;
    if (bytes + size > target) break;
    bytes += size;
    index += character.length;
  }
  return index;
}

function facetHref(facet: RichTextFacet, token: string): string | undefined {
  const feature = facet.features?.find(item => {
    if (item.$type === 'app.bsky.richtext.facet#link') {
      try {
        const url = new URL(item.uri || '');
        return url.protocol === 'https:' || url.protocol === 'http:';
      } catch {
        return false;
      }
    }
    return item.$type === 'app.bsky.richtext.facet#mention'
      || item.$type === 'app.bsky.richtext.facet#tag';
  });
  if (feature?.$type === 'app.bsky.richtext.facet#link') return feature.uri;
  if (feature?.$type === 'app.bsky.richtext.facet#mention') {
    return profileSearch(token.replace(/^@/, ''));
  }
  if (feature?.$type === 'app.bsky.richtext.facet#tag') {
    return searchSearch(feature.tag || token.replace(/^#/, ''));
  }
  return undefined;
}

export function RichText({
  text = '',
  facets = [],
  autoLink = false,
}: {
  text?: string;
  facets?: RichTextFacet[];
  autoLink?: boolean;
}) {
  const location = useLocation();
  const parts = useMemo(() => {
    const output: React.ReactNode[] = [];
    const appendPlainText = (value: string, keyPrefix: string) => {
      if (!autoLink) {
        output.push(value);
        return;
      }
      const pattern = /https?:\/\/[^\s<>"']+|@[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?/gi;
      let plainCursor = 0;
      let match: RegExpExecArray | null;
      while ((match = pattern.exec(value)) !== null) {
        output.push(value.slice(plainCursor, match.index));
        const rawToken = match[0];
        const token = rawToken.startsWith('@')
          ? rawToken
          : rawToken.replace(/[.,!?;:)\]}]+$/g, '');
        const trailing = rawToken.slice(token.length);
        if (token.startsWith('@')) {
          output.push(
            <Link
              key={`${keyPrefix}-${match.index}`}
              to={{ pathname: location.pathname, search: profileSearch(token.slice(1)) }}
            >
              {token}
            </Link>,
          );
        } else {
          output.push(
            <a
              key={`${keyPrefix}-${match.index}`}
              href={token}
              target="_blank"
              rel="noreferrer"
            >
              {token}
            </a>,
          );
        }
        output.push(trailing);
        plainCursor = match.index + rawToken.length;
      }
      output.push(value.slice(plainCursor));
    };
    let cursor = 0;
    [...facets]
      .sort((left, right) => left.index.byteStart - right.index.byteStart)
      .forEach((facet, index) => {
        const start = stringIndexAtByte(text, facet.index.byteStart);
        const end = stringIndexAtByte(text, facet.index.byteEnd);
        if (start < cursor || end <= start) return;
        appendPlainText(text.slice(cursor, start), `plain-${index}`);
        const token = text.slice(start, end);
        const href = facetHref(facet, token);
        if (href?.startsWith('?')) {
          output.push(<Link key={`${start}-${index}`} to={{ pathname: location.pathname, search: href }}>{token}</Link>);
        } else if (href) {
          output.push(
            <a key={`${start}-${index}`} href={href} target="_blank" rel="noreferrer">
              {token}
            </a>,
          );
        } else {
          output.push(token);
        }
        cursor = end;
      });
    appendPlainText(text.slice(cursor), 'plain-end');
    return output;
  }, [autoLink, facets, location.pathname, text]);
  return <>{parts}</>;
}

export function blueskyPostUrl(post: FeedPost): string {
  const rkey = post.uri.split('/').pop() || '';
  return `https://bsky.app/profile/${encodeURIComponent(post.author.handle)}/post/${rkey}`;
}

export function PostEmbed({
  post,
  onOpenPost,
  onMute,
}: {
  post: FeedPost;
  onOpenPost?: (post: FeedPost) => void;
  onMute?: (person: ActorProfile) => void;
}) {
  const embed = post.embed;
  if (!embed) return null;
  const media = embed.media || embed;
  if (media.images?.length) {
    return (
      <div className="modern-post-images">
        {media.images.slice(0, 4).map((image, index) => (
          <a href={image.fullsize || image.thumb} target="_blank" rel="noreferrer" key={`${image.fullsize}-${index}`}>
            <img
              src={image.thumb || image.fullsize}
              alt={image.alt || ''}
              loading="lazy"
              decoding="async"
            />
          </a>
        ))}
      </div>
    );
  }
  if (media.playlist) {
    return (
      <Suspense fallback={media.thumbnail
        ? <img src={media.thumbnail} alt="" style={{ display: 'block', width: '100%' }} />
        : null}
      >
        <InlinePostVideo
          playlist={media.playlist}
          thumbnail={media.thumbnail}
          postUrl={blueskyPostUrl(post)}
        />
      </Suspense>
    );
  }
  if (media.external?.uri) {
    return (
      <a className="modern-external-card" href={media.external.uri} target="_blank" rel="noreferrer">
        {media.external.thumb && <img src={media.external.thumb} alt="" loading="lazy" decoding="async" />}
        <span>
          <strong>{media.external.title || media.external.uri}</strong>
        </span>
      </a>
    );
  }
  if (embed.record?.uri) {
    return (
      <Suspense fallback={(
        <div className="modern-quote-card">
          <p className="modern-quote-text">{embed.record.value?.text || 'Opening quoted post…'}</p>
        </div>
      )}>
        <QuotedPostCard
          post={post}
          quoted={embed.record}
          onOpenPost={onOpenPost}
          onMute={onMute}
        />
      </Suspense>
    );
  }
  return null;
}

export function formatDate(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function EngagementSummary({
  actors,
  complete,
  currentHandle,
}: {
  actors: ActorProfile[];
  complete: boolean;
  currentHandle?: string;
}) {
  const location = useLocation();
  if (actors.length === 0) return null;
  const visible = actors.slice(0, 2);
  const hasMore = !complete || actors.length > visible.length;
  const names = visible.map((actor, index) => (
    <span key={actor.did || actor.handle}>
      {index > 0 && (hasMore ? ', ' : ' and ')}
      <Link to={{ pathname: location.pathname, search: profileSearch(actor.handle) }}>
        {actor.handle.toLowerCase() === currentHandle?.toLowerCase()
          ? 'you'
          : actor.displayName || actor.handle}
      </Link>
    </span>
  ));
  const remaining = actors.length - visible.length;
  return (
    <>
      {names}
      {!complete
        ? <> and others</>
        : remaining > 0 && <> and {remaining} {remaining === 1 ? 'other' : 'others'}</>}
    </>
  );
}

export function PostInteractions({
  post,
  defaultRepliesOpen = false,
}: {
  post: FeedPost;
  defaultRepliesOpen?: boolean;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [likeUri, setLikeUri] = useState(post.viewer?.like);
  const [repostUri, setRepostUri] = useState(post.viewer?.repost);
  const [likeCount, setLikeCount] = useState(post.likeCount || 0);
  const [repostCount, setRepostCount] = useState(post.repostCount || 0);
  const [replyCount, setReplyCount] = useState(post.replyCount || 0);
  const [heartAnimation, setHeartAnimation] = useState(0);
  const [repliesOpen, setRepliesOpen] = useState(defaultRepliesOpen);
  const [pending, setPending] = useState<string>();
  const [error, setError] = useState<string>();
  const likers = useQuery({
    queryKey: ['post-likers', post.uri],
    queryFn: ({ signal }) => publicBluesky.getPostLikers(post.uri, 100, signal),
    enabled: likeCount > 0,
    staleTime: 30_000,
  });
  const reposters = useQuery({
    queryKey: ['post-reposters', post.uri],
    queryFn: ({ signal }) => publicBluesky.getPostReposters(post.uri, 100, signal),
    enabled: repostCount > 0,
    staleTime: 30_000,
  });
  const engagers = useMemo(() => {
    const unique = new Map<string, ActorProfile>();
    [...(likers.data || []), ...(reposters.data || [])].forEach(actor => {
      unique.set(actor.did || actor.handle.toLowerCase(), actor);
    });
    return [...unique.values()];
  }, [likers.data, reposters.data]);
  useEffect(() => {
    setLikeUri(post.viewer?.like);
    setRepostUri(post.viewer?.repost);
    setLikeCount(post.likeCount || 0);
    setRepostCount(post.repostCount || 0);
    setReplyCount(post.replyCount || 0);
  }, [post.likeCount, post.replyCount, post.repostCount, post.uri, post.viewer?.like, post.viewer?.repost]);
  useEffect(() => {
    setRepliesOpen(defaultRepliesOpen);
  }, [defaultRepliesOpen, post.uri]);
  const react = (type: 'like' | 'repost', uri?: string) => {
    if (!auth.rpc || !auth.did) return;
    const wasActive = Boolean(uri);
    setPending(type);
    setError(undefined);
    void toggleReaction(auth.rpc, auth.did, post, type, uri)
      .then(result => {
        if (type === 'like') {
          setLikeUri(result);
          setLikeCount(value => Math.max(0, value + (wasActive ? -1 : 1)));
          if (!wasActive && result) setHeartAnimation(value => value + 1);
          void queryClient.invalidateQueries({ queryKey: ['post-likers', post.uri] });
        } else {
          setRepostUri(result);
          setRepostCount(value => Math.max(0, value + (wasActive ? -1 : 1)));
          void queryClient.invalidateQueries({ queryKey: ['post-reposters', post.uri] });
        }
      })
      .catch(reason => setError(reason instanceof Error ? reason.message : `Could not ${type} this post.`))
      .finally(() => setPending(undefined));
  };
  return (
    <div className="modern-post-interactions">
      <div className="like-section">
        <button
          type="button"
          className="like-button"
          disabled={!auth.rpc || !auth.did || pending === 'like'}
          onClick={() => react('like', likeUri)}
          aria-label={likeUri ? 'Unlike this post' : 'Like this post'}
        >
          <svg
            className="heart-icon filled-heart"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              fill={likeUri ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
          {heartAnimation > 0 && (
            <svg
              key={heartAnimation}
              className="heart-icon modern-heart-burst heart-grow-fade"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor" />
            </svg>
          )}
          <span className="like-count"><strong>{likeCount}</strong></span>
        </button>
        <button
          type="button"
          className="like-button"
          disabled={!auth.rpc || !auth.did || pending === 'repost'}
          onClick={() => react('repost', repostUri)}
          aria-label={repostUri ? 'Undo repost' : 'Repost this post'}
          aria-pressed={Boolean(repostUri)}
        >
          <svg
            className="heart-icon"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M16.793 2.293a1 1 0 0 1 1.414 0L20.5 4.586a2 2 0 0 1 0 2.828l-2.293 2.293a1 1 0 0 1-1.414-1.414L18.086 7H7a2 2 0 0 0-2 2v2a1 1 0 1 1-2 0V9a4 4 0 0 1 4-4h11.086l-1.293-1.293a1 1 0 0 1 0-1.414ZM20 12a1 1 0 0 1 1 1v2a4 4 0 0 1-4 4H5.914l1.293 1.293a1 1 0 1 1-1.414 1.414L3.5 19.414a2 2 0 0 1 0-2.828l2.293-2.293a1 1 0 0 1 1.414 1.414L5.914 17H17a2 2 0 0 0 2-2v-2a1 1 0 0 1 1-1Z"
              fill="currentColor"
            />
          </svg>
          <span><strong>{repostCount}</strong></span>
        </button>
        <span className="like-summary">
          <EngagementSummary
            actors={engagers}
            complete={likeCount <= 100 && repostCount <= 100}
            currentHandle={auth.handle}
          />
        </span>
      </div>
      <button
        type="button"
        className="classic-text-button modern-post-reply-toggle"
        style={{ display: 'block', marginTop: 8, fontSize: 11 }}
        aria-expanded={repliesOpen}
        aria-controls={`replies-${post.cid}`}
        onClick={() => setRepliesOpen(value => !value)}
      >
        {repliesOpen
          ? 'Hide replies'
          : replyCount === 0
            ? 'Reply'
            : `Show ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
      </button>
      {repliesOpen && (
        <Suspense fallback={<p className="modern-post-replies-state">Opening replies…</p>}>
          <div id={`replies-${post.cid}`}>
            <PostReplies
              post={post}
              onReplyPosted={() => setReplyCount(value => value + 1)}
              renderReply={item => <CommentCard post={item} key={item.uri} />}
            />
          </div>
        </Suspense>
      )}
      {error && <span className="modern-action-error" role="alert">{error}</span>}
    </div>
  );
}

export function PostsSection({
  posts,
  firstName,
  composer,
}: {
  posts: FeedPost[];
  firstName: string;
  composer?: React.ReactNode;
}) {
  const location = useLocation();
  const [visible, setVisible] = useState(1);
  useEffect(() => setVisible(1), [posts[0]?.uri]);
  if (posts.length === 0) {
    return (
      <>
        <p>There&apos;s nothing here yet.</p>
        {composer && <div className="modern-post-composer-slot">{composer}</div>}
      </>
    );
  }
  return (
    <>
      {posts.slice(0, visible).map(post => (
        <article className="modern-profile-post" key={post.uri}>
          {post.record.createdAt && (
            <div className="post-date">
              <a
                href={blueskyPostUrl(post)}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'inherit' }}
              >
                {formatDate(post.record.createdAt)}
              </a>
            </div>
          )}
          <p className="modern-rich-text">
            <RichText text={post.record.text} facets={post.record.facets} autoLink />
          </p>
          <PostEmbed post={post} />
          <PostInteractions post={post} />
        </article>
      ))}
      {composer && <div className="modern-post-composer-slot">{composer}</div>}
      {visible < posts.length && (
        <div id="view-more-link">
          <button type="button" className="classic-text-button" onClick={() => setVisible(value => value + 5)}>
            View more of {firstName}&apos;s posts
          </button>
        </div>
      )}
    </>
  );
}

export function TopFriends({
  firstName,
  friendCount,
  friends,
  pending,
  supporters = [],
  headingAction,
  placeholderCount = 8,
}: {
  firstName: string;
  friendCount?: number;
  friends: ActorProfile[];
  pending: boolean;
  supporters?: string[];
  headingAction?: ReactNode;
  placeholderCount?: number;
}) {
  const location = useLocation();
  const heading = (
    <h2>
      {pending ? (
        `Finding ${firstName}'s friends…`
      ) : (
        friendCount === undefined ? (
          <>{firstName}&apos;s friends</>
        ) : (
          <>
            {firstName} has{' '}
            <span className="modern-friend-count">
              {new Intl.NumberFormat('en-GB').format(friendCount)}
            </span>
            {' '}{friendCount === 1 ? 'friend' : 'friends'}
          </>
        )
      )}
    </h2>
  );
  return (
    <>
      {headingAction ? (
        <div className="modern-top8-heading">
          {heading}
          {headingAction}
        </div>
      ) : heading}
      <div className="friends-grid" aria-busy={pending}>
        {pending && Array.from({ length: placeholderCount }, (_, index) => (
          <div className="friend modern-skeleton-friend" aria-hidden="true" key={index}>
            <span className="modern-skeleton-line is-short" />
            <span className="modern-skeleton-friend-picture modern-skeleton-block" />
          </div>
        ))}
        {!pending && friends.length === 0 && <p>No Top 8 selected yet.</p>}
        {friends.map(friend => (
          <div
            className={`friend${supporters.includes(friend.handle.toLowerCase()) ? ' gold' : ''}`}
            key={friend.did || friend.handle}
          >
            <Link
              className="friend-name-link"
              title={friend.displayName || friend.handle}
              to={{ pathname: location.pathname, search: profileSearch(friend.handle) }}
            >
              {friend.displayName || friend.handle}
            </Link>
            <Link to={{ pathname: location.pathname, search: profileSearch(friend.handle) }}>
              <img
                src={friend.avatar || '../placeholder.png'}
                alt={`@${friend.handle}`}
                width="110"
                height="104"
                loading="lazy"
                decoding="async"
              />
            </Link>
          </div>
        ))}
      </div>
    </>
  );
}

export function CommentCard({
  post,
  profileHandle,
}: {
  post: FeedPost;
  profileHandle?: string;
}) {
  const location = useLocation();
  const name = post.author.displayName || post.author.handle;
  return (
    <article className="comment">
      <div className="comment-left">
        <Link
          className="comment-author-link"
          to={{ pathname: location.pathname, search: profileSearch(post.author.handle) }}
        >
          <div className="comment-author-name">{name}</div>
          <img
            className="comment-author-pic"
            src={post.author.avatar || '../placeholder.png'}
            alt={`@${post.author.handle}`}
            width="72"
            height="72"
            loading="lazy"
            decoding="async"
          />
        </Link>
      </div>
      <div className="comment-right">
        {post.record.createdAt && <div className="comment-datetime"><b>{formatDate(post.record.createdAt)}</b></div>}
        <p className="comment-text modern-rich-text">
          <RichText
            text={commentText(post.record.text || '', profileHandle)}
            facets={profileHandle ? [] : post.record.facets}
          />
        </p>
      </div>
    </article>
  );
}

export function Replies({
  threads,
  profileHandle,
  firstName,
}: {
  threads: ThreadViewPost[];
  profileHandle: string;
  firstName: string;
}) {
  const replies = threads.flatMap(thread => thread.replies || [])
    .filter(reply => reply.post.author.handle.toLowerCase() !== profileHandle.toLowerCase())
    .map(reply => reply.post);
  const uniqueReplies = [...new Map(replies.map(post => [post.uri, post])).values()];
  if (uniqueReplies.length === 0) return null;
  return (
    <section id="replies-section">
      <div className="section-header">{firstName}&apos;s Replies</div>
      <div className="replies-section modern-framed-panel">
        {uniqueReplies.map(post => <CommentCard post={post} key={post.uri} />)}
      </div>
    </section>
  );
}
